import { KaraokeClip } from '@src/editor/models/karaoke/KaraokeClip';
import { KaraokeTrack } from '@src/editor/models/karaoke/KaraokeTrack';
import {
  segmentTokens,
  squashOrphanedSegments,
} from '@src/editor/services/karaokeSegmentation';
import { v4 as uuid } from 'uuid';

import { AbstractCommand } from '../AbstractCommand';

import type { KaraokeConfig } from '@src/editor/models/karaoke/KaraokeConfig';
import type { TranscriptToken } from '@src/editor/types';
import type { VideoEditor } from '@src/editor/VideoEditor';

/**
 * ProduceKaraokeCommand - Creates a new KaraokeTrack from the active transcript
 *
 * This command:
 * 1. Gets the active transcript tokens
 * 2. Segments them into KaraokeClips using logic similar to KaraokeProducer.assembleKaraokeClips
 * 3. Creates a new KaraokeTrack with these clips
 * 4. Adds the track to the timeline
 */
export class ProduceKaraokeCommand extends AbstractCommand {
  getName(): string {
    return 'ProduceKaraokeCommand';
  }

  private karaokeConfig: KaraokeConfig;
  private createdTrack: KaraokeTrack | null = null;
  private trackNumber: number;

  constructor(editor: VideoEditor, trackNumber: number) {
    super(editor);

    // Get default karaoke config or throw if not available
    const defaultConfig = this.editor.getDefaultKaraokeConfig();
    if (!defaultConfig) {
      throw new Error('No default karaoke config available');
    }
    this.karaokeConfig = defaultConfig;
    this.trackNumber = trackNumber;
  }

  execute(): boolean {
    // Get active transcript tokens (removed tokens are already filtered out)
    const activeTranscriptTokens = this.editor.getActiveTranscriptNew();
    if (!activeTranscriptTokens || activeTranscriptTokens.length === 0) {
      console.error('No active transcript tokens available');
      return false;
    }

    // Segment tokens into karaoke clips
    const karaokeSegments = segmentTokens(activeTranscriptTokens);

    let karaokeClips: KaraokeClip[] = karaokeSegments
      .map(segment =>
        this.createClipFromTokens(
          segment.tokens,
          segment.startTime,
          segment.endTime,
        ),
      )
      .filter((clip): clip is KaraokeClip => !!clip);

    karaokeClips = squashOrphanedSegments(karaokeClips);

    if (karaokeClips.length === 0) {
      console.error('No karaoke clips generated');
      return false;
    }

    // Create KaraokeTrack
    this.createdTrack = new KaraokeTrack({
      id: String(this.trackNumber),
      type: 'text',
      karaokeClips,
    });

    // Add track to timeline
    this.editor.timeline.addExistingTrack(this.createdTrack);

    // Mark editor as dirty
    this.editor.markDirty('karaoke_produced');

    return true;
  }

  undo(): boolean {
    if (!this.createdTrack) {
      return false;
    }

    // Remove the karaoke track
    this.editor.timeline.removeTrack(this.trackNumber.toString());
    this.createdTrack = null;

    // Mark editor as dirty
    this.editor.markDirty('karaoke_removed');

    return true;
  }

  getDescription(): string {
    return 'Produce karaoke track from active transcript';
  }

  /**
   * Create a KaraokeClip from token array
   * Includes all tokens (even muted ones) but marks muted tokens in hiddenTranscriptTokenIds
   */
  private createClipFromTokens(
    tokens: TranscriptToken[],
    startTime: number | null,
    endTime: number | null,
  ): KaraokeClip | null {
    if (tokens.length === 0 || startTime === null || endTime === null) {
      return null;
    }

    if (tokens.length === 0) {
      return null;
    }

    return new KaraokeClip({
      id: uuid(),
      transcriptTokens: tokens, // Include ALL tokens
      hiddenTranscriptTokenIds: [],
      ignoredByFiltersIds: [],
      karaokeConfig: this.karaokeConfig,
      track: this.trackNumber,
    });
  }
}
